import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LoginIcon from '@mui/icons-material/Login';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { track } from '../../utils/track';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import { GiveawayCountdownTimer } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleHero/GiveawayCountdownTimer';
import GiveawayOfferSocialLinks, {
  SocialTextLink
} from '../GiveawayOffer/GiveawayOfferSocialLinks';
import {
  PAGE_BG,
  CYAN,
  GOLD,
  INK,
  BODY,
  MUTED,
  LINE,
  kickerSx,
  ctaSx,
  panelSx
} from '../GiveawayOffer/giveawayOfferTheme';
import {
  drawWhen,
  fetchJackpot,
  formatAud,
  formatDrawDate,
  promoPackReceiptFrom,
  readPromoPackReceipt,
  receiptTotals,
  rewardLines,
  usePromotion,
  type Jackpot,
  type PromoPackReceipt
} from './promoPack';

/* `/promo/confirmation` — where a paid promo pack lands.
 *
 * IT SAYS WHAT WAS BOUGHT, NOT WHAT HAS BEEN GRANTED. The card has gone through,
 * but the entries are credited by the payment webhook once the payment settles,
 * and the cards are opened in the member portal. Nothing on this page may say
 * they are already in the account.
 *
 * THE NEXT STEP DEPENDS ON THE ACCOUNT. `accountCreated` is the server's own
 * answer (`registrationCreated`): a new account has no password yet, and the
 * server has emailed it the link to set one, so it is pointed at its inbox — a
 * login page would be a dead end. A member who already had an account is pointed
 * at the login.
 *
 * COPY RULE (the QR funnel's, shared through GiveawayOfferSocialLinks): the live
 * draw is WATCHED on Facebook; Discord is the community. Nothing here may say the
 * draw is watched in Discord.
 *
 * No ad pixels, like the QR funnel's receipt pages; first-party `track()` only.
 */

const labelSx = {
  fontSize: 12.5,
  fontWeight: 800,
  letterSpacing: '0.16em',
  color: MUTED,
  mb: 1.5
} as const;

const ConfirmationContent = ({ receipt }: { receipt: PromoPackReceipt }) => {
  /* This week's rewards, again — the same read as the promo page, and always
     shown: the lines, else "Rewards to be announced" (none set yet, or no promo
     pack this week). A read that FAILED says so instead: it is no evidence
     that nothing has been announced. */
  const { load, retry } = usePromotion();
  const rewards =
    load?.status === 'ready' ? rewardLines(load.promotion.rewards) : [];

  /* The A$10,000 draw the entries go into. */
  const [jackpot, setJackpot] = useState<Jackpot | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetchJackpot(ctrl.signal).then((j) => {
      if (!ctrl.signal.aborted) setJackpot(j);
    });
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    track('promo_pack.confirmation_view', {
      packId: receipt.pack.id,
      accountCreated: receipt.accountCreated,
      bonusSelected: receipt.bonusSelected
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Keyed on the date string: the timer restarts whenever its prop changes
     identity, so it is handed one Date for the life of the page. */
  const jackpotEnd = jackpot?.endDate ?? null;
  const draw = useMemo(() => drawWhen(jackpotEnd), [jackpotEnd]);
  const prize = jackpot?.prizeAmount
    ? `A$${jackpot.prizeAmount.toLocaleString('en-AU')}`
    : null;

  const totals = receiptTotals(receipt);
  const firstName = receipt.entry.firstName;
  const bought: Array<[string, string]> = [
    [
      'Pack',
      [
        receipt.pack.title,
        receipt.bonusSelected ? 'bonus upgrade' : '',
        receipt.upsell ? 'entry boost' : ''
      ]
        .filter(Boolean)
        .join(' + ')
    ],
    ['Digital Collectibles', totals.cards.toLocaleString('en-AU')],
    ['Giveaway entries', totals.entries.toLocaleString('en-AU')],
    ['Amount paid', formatAud(receipt.amountPaidAud)]
  ];

  const steps: Array<{
    icon: ReactNode;
    title: string;
    copy: ReactNode;
    action?: ReactNode;
  }> = [
    receipt.accountCreated
      ? {
          icon: <EmailOutlinedIcon />,
          title: 'Check your email to create your password',
          copy: (
            <>
              We have emailed{' '}
              <Box
                component="span"
                sx={{ color: INK, fontWeight: 700, overflowWrap: 'anywhere' }}
              >
                {receipt.entry.email || 'you'}
              </Box>{' '}
              a link to set your password. Set it, then log in to the member
              portal to open your pack.
            </>
          )
        }
      : {
          icon: <LoginIcon />,
          title: 'Log in to open your pack',
          copy: (
            <>
              Your pack is waiting in the member portal, on the account for{' '}
              <Box
                component="span"
                sx={{ color: INK, fontWeight: 700, overflowWrap: 'anywhere' }}
              >
                {receipt.entry.email || 'your email'}
              </Box>
              .
            </>
          ),
          action: (
            <Button
              component={Link}
              to="/login"
              sx={{ ...ctaSx, mt: 2, py: 1, px: 3, fontSize: 15 }}
            >
              Log in
            </Button>
          )
        },
    {
      icon: <LiveTvOutlinedIcon />,
      title: 'Watch the live draw',
      copy: (
        <>
          Winners are drawn live on{' '}
          <SocialTextLink network="facebook" page="promo-pack-confirmation">
            Facebook
          </SocialTextLink>
          . Follow us so you see it.
        </>
      )
    },
    {
      icon: <ForumOutlinedIcon />,
      title: 'Join our Discord',
      copy: (
        <>
          Get involved with the INCRYPT community on{' '}
          <SocialTextLink network="discord" page="promo-pack-confirmation">
            Discord
          </SocialTextLink>
          .
        </>
      )
    }
  ];

  return (
    <Box sx={{ flex: 1 }}>
      {/* ── the headline ─────────────────────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 8 },
          pb: { xs: 4, md: 5 },
          textAlign: 'center'
        }}
      >
        <Typography
          sx={{ ...kickerSx, fontSize: { xs: 11, md: 12 }, mb: 1.75 }}
        >
          Payment received
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: 34, md: 52 },
            fontWeight: 900,
            lineHeight: 1.08,
            color: INK,
            mb: 1.5
          }}
        >
          You&apos;re in
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 15.5, md: 17 },
            color: BODY,
            lineHeight: 1.65,
            maxWidth: 620,
            mx: 'auto',
            overflowWrap: 'anywhere'
          }}
        >
          {firstName ? `Thanks, ${firstName}. ` : 'Thank you. '}
          Your entries are credited once your payment settles, and your pack is
          opened in the member portal.
        </Typography>
      </Box>

      {/* ── what they bought ─────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2.5, md: 4 } }}>
        <Box component="section" sx={{ ...panelSx, p: { xs: 2.5, md: 3.5 } }}>
          <Typography
            component="h2"
            sx={{ ...labelSx, textAlign: 'center', mb: 2.5 }}
          >
            WHAT YOU BOUGHT
          </Typography>

          {bought.map(([label, value], i) => (
            <Box
              key={label}
              sx={{
                /* Wraps rather than overflows: a long pack name stacks under its
                   label, kept to the right — the subscribed page's row. */
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1.6,
                borderTop: i === 0 ? 'none' : `1px solid ${LINE}`
              }}
            >
              <Typography sx={{ fontSize: 14.5, color: BODY, fontWeight: 600 }}>
                {label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 900,
                  color: i === bought.length - 1 ? GOLD : INK,
                  textAlign: 'right',
                  ml: 'auto',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflowWrap: 'anywhere'
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}

          {/* True for every account: the server credits entries only to one
              that is eligible for giveaways, whatever was ticked at checkout. */}
          <Typography
            sx={{
              mt: 1.5,
              fontSize: 13,
              color: MUTED,
              lineHeight: 1.6,
              textAlign: 'center'
            }}
          >
            Giveaway entries go to accounts that are eligible for giveaways.
          </Typography>
        </Box>
      </Box>

      {/* ── the A$10,000 draw ────────────────────────────────────────────
          Always present, and honest when there is nothing to count down to. */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 7 },
          textAlign: 'center'
        }}
      >
        <Typography sx={labelSx}>
          {(prize ? `THE ${prize} GIVEAWAY` : 'THE GIVEAWAY') +
            (draw.kind === 'countdown'
              ? ' IS DRAWN IN'
              : draw.kind === 'date'
                ? ' IS DRAWN ON'
                : '')}
        </Typography>
        {draw.kind === 'countdown' ? (
          <GiveawayCountdownTimer nextGiveaway={draw.at} />
        ) : (
          <Typography
            sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 800, color: INK }}
          >
            {draw.kind === 'date'
              ? formatDrawDate(draw.at)
              : 'The next draw date is announced soon.'}
          </Typography>
        )}
      </Box>

      {/* ── this week's rewards, again ───────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 780,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 7 }
        }}
      >
        <Box
          component="section"
          sx={{ ...panelSx, p: { xs: 2.5, md: 3.5 }, textAlign: 'center' }}
        >
          <Typography component="h2" sx={{ ...kickerSx, mb: 1 }}>
            Up for grabs in this week&apos;s promo draw
          </Typography>
          <Typography sx={{ mb: 2.25, fontSize: 14.5, lineHeight: 1.55, color: BODY }}>
            Your pack is an entry in it.
          </Typography>
          {load === null ? (
            <CircularProgress
              size={22}
              sx={{ color: CYAN }}
              aria-label="Loading this week's rewards"
            />
          ) : rewards.length > 0 ? (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {rewards.map((line, i) => (
                <Typography
                  component="li"
                  key={`${i}:${line}`}
                  sx={{
                    py: 1.25,
                    borderTop: i === 0 ? 'none' : `1px solid ${LINE}`,
                    fontSize: { xs: 17, md: 18 },
                    fontWeight: 800,
                    color: INK,
                    overflowWrap: 'anywhere'
                  }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          ) : load.status === 'error' ? (
            <>
              <Typography sx={{ fontSize: 15, color: BODY, lineHeight: 1.6 }}>
                We could not load this week&apos;s rewards.
              </Typography>
              <Button
                onClick={retry}
                sx={{
                  mt: 1,
                  color: CYAN,
                  fontWeight: 800,
                  textTransform: 'none'
                }}
              >
                Try again
              </Button>
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: { xs: 19, md: 21 },
                  fontWeight: 900,
                  color: INK
                }}
              >
                Rewards to be announced
              </Typography>
              <Typography
                sx={{ mt: 0.75, fontSize: 14.5, color: BODY, lineHeight: 1.6 }}
              >
                Set after each week&apos;s draw — check back soon.
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* ── what to do next ──────────────────────────────────────────── */}
      <Box
        sx={{
          maxWidth: 1040,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 7 }
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: 22, md: 28 },
            fontWeight: 900,
            color: INK,
            textAlign: 'center',
            mb: { xs: 3, md: 4 }
          }}
        >
          What happens next
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0,1fr)',
              md: 'repeat(3, minmax(0,1fr))'
            },
            gap: { xs: 2, md: 2.5 }
          }}
        >
          {steps.map((step, i) => (
            <Box
              key={step.title}
              sx={{ ...panelSx, p: 2.5, textAlign: 'left' }}
            >
              <Box
                sx={{
                  color: i === 0 ? CYAN : MUTED,
                  mb: 1.25,
                  '& svg': { fontSize: 24 }
                }}
              >
                {step.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: '0.14em',
                  color: i === 0 ? CYAN : MUTED,
                  mb: 0.75
                }}
              >
                STEP {i + 1}
              </Typography>
              <Typography
                sx={{ fontSize: 16.5, fontWeight: 800, color: INK, mb: 0.75 }}
              >
                {step.title}
              </Typography>
              <Typography sx={{ fontSize: 14, color: BODY, lineHeight: 1.6 }}>
                {step.copy}
              </Typography>
              {step.action}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Facebook (the live draw) and Discord (the community) ──────── */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 7 },
          pb: { xs: 6, md: 9 },
          textAlign: 'center'
        }}
      >
        <GiveawayOfferSocialLinks page="promo-pack-confirmation" />
      </Box>
    </Box>
  );
};

/* Only for a browser that has just paid — a COURTESY GATE, not security: the
 * page grants nothing, it only reads back the receipt the checkout wrote. The
 * receipt comes from router state (the checkout's navigate) or, after a reload,
 * sessionStorage; with neither, the visitor goes to `/promo`. A wrapper
 * rather than an early return, so the page's requests never run for a visitor
 * who is being sent away. */
const PromoPackConfirmationPage = () => {
  const location = useLocation();
  const receipt =
    promoPackReceiptFrom(
      (location.state as { receipt?: unknown } | null)?.receipt
    ) ?? readPromoPackReceipt();

  if (!receipt) return <Navigate replace to="/promo" />;

  return (
    <Box
      sx={{
        background: PAGE_BG,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <SiteHeader />
      <ConfirmationContent receipt={receipt} />
      <RaffleFooter />
    </Box>
  );
};

export default PromoPackConfirmationPage;
